import { z } from 'zod';

import type { TranslationKey } from '@/i18n-key';
import type { CompanyValue } from '@/types/company-values';

import { validateIconId, validateIconBackground } from '@/utils/companyValueValidation';

export const NAME_MAX_LENGTH = 50;
export const DESCRIPTION_MAX_LENGTH = 300;

const NAME_REQUIRED_MESSAGE: TranslationKey = 'visionValues.nameRequired';
const NAME_MAX_LENGTH_MESSAGE: TranslationKey = 'visionValues.nameMaxLength';
const NAME_DUPLICATE_MESSAGE: TranslationKey = 'visionValues.nameDuplicateError';
const DESCRIPTION_MAX_LENGTH_MESSAGE: TranslationKey = 'visionValues.descriptionMaxLength';

export type CompanyValueFormData = {
    name: string;
    description?: string;
    icon?: string;
    iconBackground?: string;
};

/**
 * Validates that a Company Value name is unique among existing values.
 * Excludes the current value when editing so renaming does not trigger a false duplicate.
 */
export function validateUniqueName(
    name: string,
    existingValues: CompanyValue[],
    currentValueId: string | undefined,
    currentLanguage: string,
    ctx: z.RefinementCtx,
) {
    const isDuplicate = existingValues.some((existingValue) => {
        if (currentValueId && existingValue.id === currentValueId) {
            return false;
        }

        const existingName = existingValue.name?.[currentLanguage];
        return existingName?.toLowerCase().trim() === name.toLowerCase().trim();
    });

    if (isDuplicate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: NAME_DUPLICATE_MESSAGE,
        });
    }
}

/**
 * Creates the Zod schema for the AddEditValueModal form.
 * The schema depends on runtime values (existing values, language) so it is returned as a factory.
 */
export const createCompanyValueSchema = (
    existingValues: CompanyValue[],
    currentValueId: string | undefined,
    currentLanguage: string,
) =>
    z.object({
        name: z
            .string()
            .min(1, { message: NAME_REQUIRED_MESSAGE })
            .max(NAME_MAX_LENGTH, { message: NAME_MAX_LENGTH_MESSAGE })
            .superRefine((name, ctx) =>
                validateUniqueName(name, existingValues, currentValueId, currentLanguage, ctx),
            ),
        description: z
            .string()
            .max(DESCRIPTION_MAX_LENGTH, { message: DESCRIPTION_MAX_LENGTH_MESSAGE })
            .optional(),
        icon: z.string().optional().superRefine(validateIconId),
        iconBackground: z.string().optional().superRefine(validateIconBackground),
    });
